import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Modal,
  Layout,
  Text,
  Padding,
  AnchorLink,
  Container,
  Icon,
  IconList,
} from "@kidslivesafe/sc-react";
import { selectMemberData } from "../../features/authentication/authenticationSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
  createCustomerRedisTokenAsync,
  sendMemberEventAsync,
} from "../../features/admin/adminSlice";
import { selectBrandInfo } from "../../features/brand/brandSlice";
import { useNavigate, useLocation } from "react-router-dom";
import { formatPhoneNumber } from "../../utils";
import {
  getSubscriptionDetailsAsync,
  retryPaymentAsync,
  selectPrimarySubscription,
  unsetAccelerateBilling,
} from "../../features/subscriptions/subscriptionSlice";

const AccelerateModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const pageURL = location.pathname;
  const brandInfo = useAppSelector(selectBrandInfo);
  const subscriptionDetails = useAppSelector(selectPrimarySubscription);
  const { billingDate } = useAppSelector(selectMemberData);
  const [retryClicked, setRetryClicked] = useState<boolean>(false);
  const [retrySuccess, setRetrySuccess] = useState<boolean>(false);
  const [showAccelerateModal, setShowAccelerateModal] =
    useState<boolean>(false);
  const [retryLoading, setRetryLoading] = useState<boolean>(false);

  const hasSentModalDisplayEvent = useRef(false);
  const hasSentAccelerateConfirmationEvent = useRef(false);

  useEffect(() => {
    if (showAccelerateModal && retryClicked && retrySuccess) {
      //modal charge payment successful
      if (!hasSentAccelerateConfirmationEvent.current) {
        dispatch(
          sendMemberEventAsync({
            EventCategory: "Modal Display",
            EventAction: "Accelerate Confirmation Modal",
            MetaData: JSON.stringify({
              key: "PRR18",
              value: "Accelerate Trial",
            }),
          }),
        );
        hasSentAccelerateConfirmationEvent.current = true;
        //Accelerate modal only shown once
      }
    } else if (retryClicked && !retrySuccess) {
      //modal charge payment failed
      dispatch(
        sendMemberEventAsync({
          EventCategory: "Modal Display",
          EventAction: "Payment Not Processed Modal",
          MetaData: JSON.stringify({
            key: "PRR18",
            value: "Accelerate Trial",
          }),
        }),
      );
    } else if (subscriptionDetails?.accelerateBilling) {
      if (!hasSentModalDisplayEvent.current && !retryClicked && !retrySuccess) {
        dispatch(
          sendMemberEventAsync({
            EventCategory: "Modal Display",
            EventAction: "Retry",
            MetaData: JSON.stringify({
              PageName: pageURL,
              Flow: "accelerate",
            }),
          }),
        );
        hasSentModalDisplayEvent.current = true;
      }
      setShowAccelerateModal(true);
    } else {
      setShowAccelerateModal(false);
    }
  }, [retryClicked, retrySuccess, subscriptionDetails?.accelerateBilling]);

  const handleRetryPayment = async () => {
    setRetryLoading(true);
    try {
      const email = "accelerate";
      const IgnoreBillingDateCheck = true;

      dispatch(
        sendMemberEventAsync({
          EventCategory: "Button Click",
          EventAction: "Unlock Access Now",
          MetaData: JSON.stringify({
            key: "PRR18",
            value: "Accelerate Trial",
          }),
        }),
      );
      const resultAction = await dispatch(
        retryPaymentAsync({ email, IgnoreBillingDateCheck }),
      );
      if (retryPaymentAsync.fulfilled.match(resultAction)) {
        setRetryClicked(true);
        setRetrySuccess(true);
        setRetryLoading(false);
        // get subscription details
        await dispatch(getSubscriptionDetailsAsync());

        setTimeout(() => {
          navigate("/");
        }, 0);
      } else {
        setRetryClicked(true);
        setRetrySuccess(false);
        setRetryLoading(false);
      }
    } catch (error) {
      console.error("Retry failed:", error);
    }
  };

  const handleExternalNavigation = async () => {
    const createCustomerRedisTokenAsyncResponse = await dispatch(
      createCustomerRedisTokenAsync(),
    );

    dispatch(
      sendMemberEventAsync({
        EventCategory: "Button Click",
        EventAction: "Update CC",
        MetaData: JSON.stringify({
          PageName: pageURL,
          Flow: "accelerate",
        }),
      }),
    );

    if (
      createCustomerRedisTokenAsync.fulfilled.match(
        createCustomerRedisTokenAsyncResponse,
      )
    ) {
      const uuid = createCustomerRedisTokenAsyncResponse.payload.token;
      window.location.href = brandInfo.homeUrl + "/fix-payment?uuid=" + uuid;
    }
  };
  return (
    <>
      <Modal
        open={showAccelerateModal}
        setOpen={() => {
          dispatch(unsetAccelerateBilling());
          dispatch(
            sendMemberEventAsync({
              EventCategory: "Button Click",
              EventAction: "Exit Accelerate",
              MetaData: JSON.stringify({
                key: "PRR18",
                value: "Accelerate Trial",
              }),
            }),
          );
          setShowAccelerateModal(false);
        }}
        size="fit-content"
        xClose={false}
        outSideClose={false}
        scrollable={false}
      >
        <Padding
          space="sm"
          top="xxs"
          breakpointStyles={{ mobile: { space: "none" } }}
        >
          <Layout
            type="flex"
            flexProps={{
              direction: "column",
              gap: "md",
              alignItems: "center",
            }}
            breakpointStyles={{
              desktop: {
                type: "flex",
                flexProps: {
                  direction: "column",
                  gap: "xl",
                  alignItems: "center",
                },
              },
            }}
          >
            {!retryClicked &&
            !retrySuccess &&
            subscriptionDetails?.accelerateBilling ? (
              <>
                <Layout
                  type="flex"
                  flexProps={{
                    direction: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "sm",
                  }}
                  breakpointStyles={{
                    desktop: {
                      type: "flex",
                      flexProps: {
                        direction: "column",
                        gap: "xl",
                        alignItems: "center",
                      },
                    },
                  }}
                >
                  <Container altColor="alt" bodyBorderRadius>
                    <Padding space="xxs" left="xs" right="xs">
                      <Text textAlign="center" size="sm" fontWeight="semibold">
                        Report Limit Reached
                      </Text>
                    </Padding>
                  </Container>

                  <div>
                    <Text textAlign="center" tag="h2">
                      Unlock Full Access
                    </Text>
                  </div>
                  <Text textAlign="center" size="lg" fontWeight="semibold">
                    Get 100 More Reports Now
                  </Text>
                  <Container altColor="alt" bodyBorderRadius>
                    <Padding space="sm" left="xl" right="xl">
                      <Layout
                        type="flex"
                        flexProps={{
                          gap: "xs",
                          alignItems: "flex-start",
                          direction: "column",
                        }}
                      >
                        <Layout
                          type="flex"
                          flexProps={{
                            gap: "xs",
                            alignItems: "center",
                          }}
                        >
                          <Icon name={IconList.IconCircle} size={6} />
                          <Text fontWeight="semibold" size="sm">
                            Access up to 100 reports every month
                          </Text>
                        </Layout>
                        <Layout
                          type="flex"
                          flexProps={{
                            gap: "xs",
                            alignItems: "center",
                          }}
                        >
                          <Icon name={IconList.IconCircle} size={6} />
                          <Text fontWeight="semibold" size="sm">
                            Full access to all premium features
                          </Text>
                        </Layout>
                      </Layout>
                    </Padding>
                  </Container>
                  <Text textAlign="center" size="sm">
                    Your trial isn't over. You can still view reports already
                    accessed.
                  </Text>
                </Layout>
                <Layout
                  type="flex"
                  flexProps={{
                    direction: "column",
                    gap: "md",
                    alignItems: "center",
                  }}
                  fillHorizontal
                >
                  <Button
                    buttonText="Yes, Access 100 Reports Now"
                    fill="fit-content"
                    handleClick={handleRetryPayment}
                    clicked={retryClicked}
                    loading={retryLoading}
                  />
                  <AnchorLink
                    handleClick={() => {
                      dispatch(unsetAccelerateBilling());
                      dispatch(
                        sendMemberEventAsync({
                          EventCategory: "Button Click",
                          EventAction: "Continue Trial",
                          MetaData: JSON.stringify({
                            key: "PRR18",
                            value: "Accelerate Trial",
                          }),
                        }),
                      );
                      setShowAccelerateModal(false);
                    }}
                    stayUnderline
                    role="button"
                  >
                    No, I Don't Need More Reports
                  </AnchorLink>
                </Layout>

                <Text textAlign="center" size="base">
                  By clicking "Yes, Access 100 Reports Now", you agree to be
                  billed ${subscriptionDetails?.price} immediately and gain
                  access to 100 monthly reports.
                </Text>

                <Text textAlign="center" size="sm">
                  Closing this window will return you to your trial. For
                  questions, contact support at{" "}
                  <AnchorLink
                    inline
                    phoneNumber
                    hoverUnderline
                    href={brandInfo.phoneNumber}
                  >
                    {formatPhoneNumber(brandInfo.phoneNumber)}
                  </AnchorLink>
                  .
                </Text>
              </>
            ) : retryClicked && !retrySuccess ? (
              <>
                <Layout
                  type="flex"
                  flexProps={{
                    direction: "column",
                    gap: "sm",
                  }}
                >
                  <div>
                    <Text textAlign="center" tag="h2" fontWeight="semibold">
                      We've encountered a problem!{" "}
                    </Text>
                  </div>
                  <Text textAlign="center" size="sm">
                    We couldn’t process your payment, possibly due to an expired
                    or declined card.
                  </Text>
                  <Text textAlign="center" size="sm">
                    Please update your payment details to maintain access to
                    your full subscription.
                  </Text>
                </Layout>
                <Button
                  buttonText="Update Payment Method"
                  handleClick={handleExternalNavigation}
                  size="md"
                  fill="fill"
                />
                <Text textAlign="center" size="sm">
                  For questions please contact member support at{" "}
                  <AnchorLink
                    inline
                    phoneNumber
                    hoverUnderline
                    href={brandInfo.phoneNumber}
                  >
                    {formatPhoneNumber(brandInfo.phoneNumber)}
                  </AnchorLink>
                  .
                </Text>
              </>
            ) : retryClicked && retrySuccess && showAccelerateModal ? (
              <>
                <Layout
                  type="flex"
                  flexProps={{
                    direction: "column",
                    gap: "lg",
                  }}
                >
                  <div>
                    <Text textAlign="center" tag="h2" fontWeight="semibold">
                      You're All Set!{" "}
                    </Text>
                    <Padding top="lg">
                      <Layout
                        type="flex"
                        flexProps={{ direction: "column", gap: "lg" }}
                      >
                        <Text textAlign="center" size="sm">
                          Your subscription is now active, and you've unlocked
                          full access to {brandInfo.name}.
                        </Text>
                        <Text textAlign="center" size="sm">
                          Your subscription renews monthly with your next
                          billing date on {billingDate}.
                        </Text>
                      </Layout>
                    </Padding>
                  </div>
                </Layout>
                <Button
                  buttonText="Go to Dashboard"
                  handleClick={() => {
                    dispatch(unsetAccelerateBilling());
                    dispatch(
                      sendMemberEventAsync({
                        EventCategory: "Button Click",
                        EventAction: "Exit Accelerate",
                        MetaData: JSON.stringify({
                          key: "PRR18",
                          value: "Accelerate Trial",
                        }),
                      }),
                    );
                    setShowAccelerateModal(false);
                  }}
                  color="success"
                  cursor="pointer"
                />
              </>
            ) : null}
          </Layout>
        </Padding>
      </Modal>
    </>
  );
};
export default AccelerateModal;
